import { NodeDocContent } from "./nodeDocsData";

export const triggerNodesDocs: Record<string, NodeDocContent> = {
  "FILE-UPLOAD": {
    title: "On Document Upload",
    subtitle: "File Upload Trigger",
    color: "green",
    badge: "Trigger",
    description:
      "Triggers workflow execution when a document is uploaded directly to the node. This is the most common starting point for document processing pipelines.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          <strong>Supported formats:</strong> PDF, JPG, PNG, DOCX, DOC, XLSX, CSV. Maximum file size is governed by your system settings.
        </div>

        <section>
          <h2>How it works</h2>
          <p>When a file is uploaded to this node — either via drag-and-drop on the canvas or through the file browser — the node extracts metadata and passes both the file and its properties downstream.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Upload a file</p>
                <p class="wf-step-desc">Drag and drop a file onto the node, or click to open the file browser.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Metadata extraction</p>
                <p class="wf-step-desc">The node reads filename, MIME type, file size, and page count automatically.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Trigger downstream</p>
                <p class="wf-step-desc">File data and metadata are passed to all connected nodes.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>File</code></td>
                <td>The document to upload. Accepts PDF, JPG, PNG, DOCX, DOC, XLSX, and CSV formats.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Output</h2>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>file_name</code></td><td>string</td><td>Original filename as uploaded</td></tr>
              <tr><td><code>key</code></td><td>string</td><td>Unique storage key assigned to the file</td></tr>
              <tr><td><code>url</code></td><td>string</td><td>Accessible URL for the stored file</td></tr>
              <tr><td><code>file_type</code></td><td>string</td><td>MIME type, e.g. <code>application/pdf</code></td></tr>
              <tr><td><code>file_size</code></td><td>number</td><td>File size in bytes</td></tr>
              <tr><td><code>file_pages</code></td><td>number</td><td>Page count (PDF and DOCX only, <code>null</code> for other formats)</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Invoice processing</p>
              <p class="wf-use-case-flow">Upload &rarr; OCR &rarr; Smart Parse &rarr; Save to Database</p>
              <p>Upload invoice PDFs and automatically extract vendor, amount, date, and line items into structured data.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Document comparison</p>
              <p class="wf-use-case-flow">Upload Source &rarr; Upload Target &rarr; Compare &rarr; Export Diff</p>
              <p>Use two upload nodes to feed source and target documents into a comparison workflow.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>This is typically the first node in any document processing pipeline.</li>
            <li>Connect directly to OCR or Smart Parse nodes for the fastest results.</li>
            <li>Supports canvas drag-and-drop — just drag files onto the node.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "DOCUMENT-STORE": {
    title: "DocIQ Trigger",
    subtitle: "Document Store Trigger",
    color: "blue",
    badge: "Trigger",
    description:
      "Connects to pre-configured document stores and retrieves documents for downstream processing. Select a store and all documents become available for your workflow.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          Configure your document stores in the <strong>Documents</strong> section before using this node.
        </div>

        <section>
          <h2>How it works</h2>
          <p>This node connects to an existing document store — a pre-configured collection of files — and makes the entire collection available for processing within the workflow.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Select a document store</p>
                <p class="wf-step-desc">Choose from the dropdown of configured stores in the node settings.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Fetch documents</p>
                <p class="wf-step-desc">All files from the selected store are loaded automatically when the workflow runs.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Process downstream</p>
                <p class="wf-step-desc">Structured document data — metadata, summaries, and content — flows to connected nodes.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Document Store</code></td>
                <td>Select from the dropdown of pre-configured document stores. Stores are managed in the Documents section.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Output</h2>
          <table>
            <thead>
              <tr>
                <th>Field</th>
                <th>Type</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>document_store_id</code></td><td>string</td><td>Unique identifier of the selected store</td></tr>
              <tr><td><code>outputs</code></td><td>object</td><td>Structured data from all documents including metadata, summaries, and parsed content</td></tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>When to use</h2>
          <p>Use this node instead of File Upload when you need to process documents that are already stored in the system. It's ideal for:</p>
          <ul class="wf-list">
            <li><strong>Batch processing</strong> — run a workflow across an entire document collection at once.</li>
            <li><strong>Reprocessing</strong> — re-run extraction or analysis on previously uploaded documents.</li>
            <li><strong>Scheduled workflows</strong> — pair with a scheduler to periodically process new additions to a store.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Use "View Outputs" to inspect available data before connecting downstream nodes.</li>
            <li>Combine with a For-Each node to process documents individually.</li>
            <li>Ideal for batch processing workflows on pre-uploaded documents.</li>
          </ul>
        </div>
      </div>
    `,
  },

  HTTP: {
    title: "HTTP Trigger",
    subtitle: "Public API Endpoint",
    color: "amber",
    badge: "Trigger",
    description:
      "Exposes your workflow as a public API endpoint. Once configured and saved, it generates a unique URL that any external system can call to trigger the workflow. This effectively turns your workflow into an API service.",
    content: `
      <div class="space-y-10">
        <div class="wf-callout wf-callout-info">
          This node makes your workflow callable from <strong>any application</strong> — frontend apps, backend services, mobile apps, third-party webhooks, cron schedulers, or API tools like Postman. The request method is always <strong>POST</strong>.
        </div>

        <section>
          <h2>How it works</h2>
          <p>You configure an endpoint path, save the workflow, and the system generates a unique URL. When an external system sends a POST request to that URL, the workflow starts executing and the request data (body, headers) becomes the workflow input for all downstream nodes.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Configure endpoint path</p>
                <p class="wf-step-desc">Set the path (e.g. <code>/execute</code>) in the node settings. This defines the route for your workflow's API.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Save the workflow</p>
                <p class="wf-step-desc">After saving, the system generates a full endpoint URL with a unique request ID.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">External system calls the URL</p>
                <p class="wf-step-desc">Any application sends a <strong>POST</strong> request to the generated URL with a JSON body.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">Workflow executes</p>
                <p class="wf-step-desc">The request body, headers, and parameters are passed as input to all downstream nodes.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Generated endpoint</h2>
          <p>After saving the workflow, the full endpoint URL follows this pattern:</p>
          <div class="wf-code-block">
            <div class="wf-code-header">Generated URL</div>
            <pre><code>https://your-domain.com/workflow-http-node-srv/execute/&lt;request_id&gt;</code></pre>
          </div>
          <p>The <code>request_id</code> is auto-generated and unique to this workflow instance. Copy the full URL from the node's settings panel after saving.</p>
        </section>

        <section>
          <h2>Example request</h2>
          <p>Calling the endpoint from any application (React, NestJS, Python, Postman, or any HTTP client):</p>
          <div class="wf-code-block">
            <div class="wf-code-header">JavaScript / fetch</div>
            <pre><code>fetch("https://your-domain.com/workflow-http-node-srv/execute/abc123", {
  method: "POST",
  headers: {
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    name: "John",
    documentId: "DOC001"
  })
})</code></pre>
          </div>
          <p>This will trigger the workflow and pass <code>name</code> and <code>documentId</code> as input data that downstream nodes can reference.</p>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Endpoint Path</code></td>
                <td>The route path for the API endpoint (e.g. <code>/execute</code>). Combined with the base URL to form the full endpoint.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Input data</h2>
          <p>External callers can pass data to the workflow through:</p>
          <table>
            <thead>
              <tr>
                <th>Source</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr><td><code>Request body</code></td><td>JSON payload sent in the POST body — the primary way to pass structured data</td></tr>
              <tr><td><code>Query parameters</code></td><td>URL query strings appended to the endpoint (e.g. <code>?key=value</code>)</td></tr>
              <tr><td><code>Headers</code></td><td>Custom HTTP headers included in the request</td></tr>
            </tbody>
          </table>
          <p>All of this data is available to downstream nodes as workflow input.</p>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Frontend-triggered processing</p>
              <p class="wf-use-case-flow">React App &rarr; HTTP Trigger &rarr; OCR &rarr; Smart Extract &rarr; MongoDB</p>
              <p>A React frontend submits a document ID via POST. The workflow fetches, processes, and stores the extracted data automatically.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Webhook receiver</p>
              <p class="wf-use-case-flow">Payment Gateway &rarr; HTTP Trigger &rarr; Transform &rarr; PDF Generator &rarr; Webhook</p>
              <p>A payment success webhook hits the endpoint. The workflow generates an invoice PDF and notifies the downstream system.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Scheduled automation</p>
              <p class="wf-use-case-flow">Cron Job &rarr; HTTP Trigger &rarr; HTTP Request &rarr; Transform &rarr; Excel Export</p>
              <p>A cron job or scheduler calls the endpoint on a schedule to fetch, process, and export data periodically.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Microservice integration</p>
              <p class="wf-use-case-flow">Backend Service &rarr; HTTP Trigger &rarr; Code Node &rarr; MongoDB &rarr; Webhook</p>
              <p>Another backend service sends data to the workflow for processing, storage, and notification — treating the workflow as a microservice.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Important</p>
          <ul>
            <li>The workflow <strong>must be saved</strong> before the endpoint URL is generated.</li>
            <li>The request method is always <strong>POST</strong> — configure your callers accordingly.</li>
            <li>Always validate incoming data in downstream nodes before processing.</li>
            <li>Consider adding authentication (token validation via Code Node or If-Else) to secure your endpoint.</li>
            <li>Use the Transform or Code Node immediately after the trigger to extract and validate the specific fields you need from the request body.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "ONEDRIVE-CONNECTOR": {
    title: "OneDrive Trigger",
    subtitle: "Microsoft OneDrive Trigger",
    color: "blue",
    badge: "Trigger",
    description:
      "Monitors a Microsoft OneDrive folder for new or modified files. When a change is detected, the workflow fires automatically. Requires OAuth authentication.",
    content: `
      <div class="space-y-10">
        <section>
          <h2>Setup</h2>
          <p>Connect your Microsoft account and select a folder to monitor. The node will automatically trigger when files are added or modified.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Open configuration</p>
                <p class="wf-step-desc">Click "Configure Trigger" on the node to open the settings modal.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Authenticate with Microsoft</p>
                <p class="wf-step-desc">Click "Connect OneDrive" and complete the OAuth flow in the popup window.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Select credential and folder</p>
                <p class="wf-step-desc">Choose the authenticated account and the target folder to watch.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">Save and activate</p>
                <p class="wf-step-desc">The trigger fires automatically when new files appear in the selected folder.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Credential</code></td>
                <td>The authenticated Microsoft account. Set up via the OAuth flow.</td>
                <td>Yes</td>
              </tr>
              <tr>
                <td><code>Folder</code></td>
                <td>The OneDrive folder to monitor for new or modified files.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>When to use</h2>
          <p>Use this trigger when documents arrive via OneDrive — common in enterprise environments where teams share files through Microsoft 365. Good scenarios include:</p>
          <ul class="wf-list">
            <li><strong>Shared folder monitoring</strong> — automatically process files as team members upload them.</li>
            <li><strong>Cross-platform pipelines</strong> — bridge OneDrive with your DMS workflows without manual uploads.</li>
            <li><strong>Compliance workflows</strong> — automatically scan and classify documents as they arrive.</li>
          </ul>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Complete OAuth authentication before selecting a folder.</li>
            <li>Select a specific subfolder rather than root for focused monitoring.</li>
            <li>Combine with OCR or Parsing nodes to auto-process uploaded documents.</li>
          </ul>
        </div>
      </div>
    `,
  },

  GMAIL_CONNECTOR: {
    title: "Gmail Trigger",
    subtitle: "Google Gmail Trigger",
    color: "red",
    badge: "Trigger",
    description:
      "Monitors your Gmail inbox and automatically starts the workflow when new emails arrive. Requires Google OAuth. Ideal for email-driven document processing.",
    content: `
      <div class="space-y-10">
        <section>
          <h2>Setup</h2>
          <p>Authenticate with your Google account to connect your Gmail inbox. Once linked, the node triggers whenever a new email arrives.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Open configuration</p>
                <p class="wf-step-desc">Click "Configure Trigger" on the node to open the Gmail settings modal.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Authenticate with Google</p>
                <p class="wf-step-desc">Click "Connect Gmail" and complete the OAuth consent flow in the popup.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Select credential</p>
                <p class="wf-step-desc">Choose the Gmail account from the credentials dropdown.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">Save and activate</p>
                <p class="wf-step-desc">New emails will automatically trigger the workflow.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Credential</code></td>
                <td>The authenticated Google account. Set up via the OAuth flow.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <section>
          <h2>Common use cases</h2>
          <div class="wf-use-cases">
            <div class="wf-use-case">
              <p class="wf-use-case-title">Email-to-OCR pipeline</p>
              <p class="wf-use-case-flow">Gmail Trigger &rarr; Email Parser &rarr; OCR &rarr; Database</p>
              <p>Automatically extract text and data from email attachments (invoices, receipts, forms) and store the structured results.</p>
            </div>
            <div class="wf-use-case">
              <p class="wf-use-case-title">Email routing</p>
              <p class="wf-use-case-flow">Gmail Trigger &rarr; IF-ELSE (by subject) &rarr; Route A / Route B</p>
              <p>Route emails to different processing branches based on subject, sender, or content keywords.</p>
            </div>
          </div>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Combine with the Email Parsing node to extract structured data from email body and attachments.</li>
            <li>Use IF-ELSE after this node to filter by subject line or sender address.</li>
            <li>Allow popups in your browser for the OAuth authentication flow.</li>
          </ul>
        </div>
      </div>
    `,
  },

  "OUTLOOK-CONNECTOR": {
    title: "Outlook Trigger",
    subtitle: "Microsoft Outlook Trigger",
    color: "indigo",
    badge: "Trigger",
    description:
      "Integrates with Microsoft Outlook to trigger workflows on incoming emails. Uses Microsoft OAuth for secure authentication. Ideal for enterprise Microsoft 365 environments.",
    content: `
      <div class="space-y-10">
        <section>
          <h2>Setup</h2>
          <p>Connect your Microsoft account to monitor your Outlook inbox. The setup process is identical to the Gmail Trigger — authenticate, select credentials, and activate.</p>
          <div class="wf-steps">
            <div class="wf-step">
              <div class="wf-step-num">1</div>
              <div>
                <p class="wf-step-title">Open configuration</p>
                <p class="wf-step-desc">Click "Configure Connector" on the node to open the Outlook settings modal.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">2</div>
              <div>
                <p class="wf-step-title">Authenticate with Microsoft</p>
                <p class="wf-step-desc">Click "Connect Outlook" and complete the OAuth consent flow in the popup.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num">3</div>
              <div>
                <p class="wf-step-title">Select credential</p>
                <p class="wf-step-desc">Choose the Outlook account from the credentials dropdown.</p>
              </div>
            </div>
            <div class="wf-step">
              <div class="wf-step-num wf-step-done">&#10003;</div>
              <div>
                <p class="wf-step-title">Save and activate</p>
                <p class="wf-step-desc">Incoming Outlook messages will trigger the workflow automatically.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2>Parameters</h2>
          <table>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Description</th>
                <th>Required</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>Credential</code></td>
                <td>The authenticated Microsoft account. Set up via the OAuth flow.</td>
                <td>Yes</td>
              </tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-info">
          This node works identically to the Gmail Trigger. Use whichever matches your organization's email provider. Both support the same downstream connections and output format.
        </div>

        <section>
          <h2>Gmail vs Outlook</h2>
          <table>
            <thead>
              <tr>
                <th>Feature</th>
                <th>Gmail Trigger</th>
                <th>Outlook Trigger</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Auth provider</td><td>Google OAuth</td><td>Microsoft OAuth</td></tr>
              <tr><td>Best for</td><td>Google Workspace orgs</td><td>Microsoft 365 orgs</td></tr>
              <tr><td>Output format</td><td colspan="2">Identical — email body, attachments, metadata</td></tr>
              <tr><td>Downstream compatibility</td><td colspan="2">Identical — same nodes work with both</td></tr>
            </tbody>
          </table>
        </section>

        <div class="wf-callout wf-callout-tip">
          <p class="wf-callout-label">Tips</p>
          <ul>
            <li>Ensure your Microsoft 365 admin has enabled the required API permissions.</li>
            <li>Chain with Email Parsing for automated email content extraction.</li>
            <li>Allow browser popups for the OAuth authentication flow.</li>
          </ul>
        </div>
      </div>
    `,
  },
};
